import _ from 'lodash'
import { withDependencies } from '@wix/thunderbolt-ioc'
import {
	IStructureStore,
	IPropsStore,
	Structure,
	Props,
	Component,
	PendingComponentClasses,
	BeckyPropsInitialStoreSym,
	IAppWillLoadPageHandler,
	CompProps,
	NavigationInfoSymbol,
	ICompActionsStore,
	CompActionsSym,
	StateRefsStoreSymbol,
	IDataRequirementsChecker,
	IStateRefsStore,
	PropsMap,
} from '@wix/thunderbolt-symbols'
import { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'
import {
	ComponentDriverProviderSymbol,
	ComponentsStore,
	ComponentsStoreSymbol,
	ComponentWillMount,
	ComponentWillMountSymbol,
	ViewerComponent,
	ViewerComponentProvider,
} from 'feature-components'
import { ComponentsLoaderSymbol, IComponentsLoader } from '@wix/thunderbolt-components-loader'
import { getCompClassType, GHOST_REF_COMP_TYPE } from '@wix/thunderbolt-commons'
import { INavigationManager, NavigationManagerSymbol } from 'feature-navigation-manager'
import { IPageProvider, IPageReflector, LogicalReflectorSymbol } from 'feature-pages'
import type { INavigationInfo } from 'ds-feature-editor-navigation'
import type { ComponentsUpdatesHandlers } from './types'
import { name } from './symbols'

const isSameCompClass = (comp1: Component, comp2: Component) =>
	comp1.componentType === comp2.componentType && comp1.uiType === comp2.uiType

const difference = (updatedProps: CompProps | Component, baseProps: CompProps | Component): CompProps | Component => {
	if (!baseProps) {
		return updatedProps || {}
	}

	return _.transform(
		updatedProps,
		(result, value, key) => {
			// We are cloning objects & arrays props in carmi so we need to use deep equality method
			// http://github.com/wix-private/thunderbolt/blob/bb331b9348611aecaf56a01087d97c185f43515a/packages/thunderbolt-components/src/index.ts#L304-L304
			if (!_.isEqual(value, _.get(baseProps, key))) {
				_.set(result, key, value)
			}
		},
		{} as CompProps | Component
	)
}

export const ComponentsUpdatesManager = withDependencies(
	[
		ComponentsLoaderSymbol,
		ComponentsStoreSymbol,
		Structure,
		Props,
		StateRefsStoreSymbol,
		CompActionsSym,
		ComponentDriverProviderSymbol,
		LogicalReflectorSymbol,
		BeckyPropsInitialStoreSym,
		NavigationManagerSymbol,
		NavigationInfoSymbol,
	],
	(
		componentsLoader: IComponentsLoader,
		componentsStore: ComponentsStore,
		structure: IStructureStore,
		props: IPropsStore,
		stateRefs: IStateRefsStore,
		compActionsStore: ICompActionsStore,
		viewerComponentProvider: ViewerComponentProvider,
		logicalProvider: IPageProvider,
		modelPropsStore: IPropsStore,
		navigationManager: INavigationManager,
		{ isDuringInitialNavigationPhase }: INavigationInfo
	): IModelUpdatesHandlersImplementor<ComponentsUpdatesHandlers> &
		IDataRequirementsChecker &
		IAppWillLoadPageHandler => {
		const ignoreComponentTypes = new Set()
		const logicalProviders: Record<string, IPageReflector> = {}

		const invokeComponentWillMountHandlersIfNeeded = async (
			contextId: string,
			compId: string,
			compStructure: Component
		) => {
			componentsStore.update(viewerComponentProvider.createComponents({ [compId]: compStructure }))

			if (ignoreComponentTypes.has(compStructure.componentType)) {
				return []
			}

			if (navigationManager.isDuringNavigation() || navigationManager.isFirstNavigation()) {
				return []
			}

			if (isDuringInitialNavigationPhase()) {
				return []
			}

			const reflector = logicalProviders[contextId] || (await logicalProvider('masterPage', 'masterPage'))
			const componentWillMountArray = reflector.getAllImplementersOnPageOf<ComponentWillMount<ViewerComponent>>(
				ComponentWillMountSymbol
			)

			const compWillMountHandlers = componentWillMountArray.filter(({ componentTypes }) =>
				componentTypes.includes(compStructure.componentType)
			)

			if (!compWillMountHandlers.length) {
				ignoreComponentTypes.add(compStructure.componentType)
				return []
			}

			const compDriver = componentsStore.get<ViewerComponent>(compId)

			return compWillMountHandlers.map(({ componentWillMount }) => componentWillMount(compDriver))
		}

		const pendingComponentClasses: PendingComponentClasses = {}

		const updatePendingComponentClasses = (loadComponentPromise: Promise<void>, compStructure: Component) => {
			const compClassType = getCompClassType(compStructure.componentType, compStructure.uiType)

			loadComponentPromise.then(() => {
				delete pendingComponentClasses[compClassType]
			})

			pendingComponentClasses[compClassType] = pendingComponentClasses[compClassType] || loadComponentPromise
		}

		const isNewComp = (compId: string, compStructureFromModel: Component) => {
			const compStructureFromStore = structure.get(compId)
			return (
				!compStructureFromStore ||
				!isSameCompClass(compStructureFromModel, compStructureFromStore) ||
				compStructureFromModel.pageId !== compStructureFromStore.pageId
			)
		}

		return {
			name: 'componentsUpdatesManager',
			appWillLoadPage: ({ contextId, pageId }) => {
				return Promise.all([
					logicalProvider(contextId, pageId),
					logicalProvider('masterPage', 'masterPage'),
				]).then(([reflector, masterPageReflector]) => {
					logicalProviders[contextId] = reflector
					logicalProviders.masterPage = masterPageReflector
				})
			},
			featureName: name,
			handlers: {
				onCompAdded: async ({ compId, compStructure, compProps, compStateRefs }) => {
					if (!isNewComp(compId, compStructure)) {
						return
					}

					const compInCurrentStructure = structure.get(compId)
					if (
						compInCurrentStructure &&
						(compInCurrentStructure.componentType === 'tpaWidgetNative' ||
							compStructure.componentType === 'tpaWidgetNative')
					) {
						// if we had a component and it's structure changed to/from tpaWidgetNative, it means we're in the midst of navigation between edit and preview modes.
						// navigation will take care of everything (platform, ooi componentWillMount) so we're short circuiting here,
						// otherwise the props.update() down the line will render the component before it's props are ready.
						return
					}

					if (compInCurrentStructure && compInCurrentStructure.pageId !== compStructure.pageId) {
						// clean previous context stores in case comp has changed its page id.
						// happens for hard coded comps like SCROLL_TO_TOP (from undefined to master page, not sure we even need to do something in that case)
						// and in sosp scenarios
						const compPageId = compStructure.pageId
						structure.updatePageId(compId, compPageId)
						props.updatePageId(compId, compPageId)
						stateRefs.updatePageId(compId, compPageId)
						compActionsStore.updatePageId(compId, compPageId)
						componentsStore.updatePageId(compId, compPageId)
						modelPropsStore.updatePageId(compId, compPageId)
					}

					if (
						compInCurrentStructure?.componentType !== GHOST_REF_COMP_TYPE &&
						compStructure?.componentType !== GHOST_REF_COMP_TYPE
					) {
						props.update({ [compId]: compProps })
						modelPropsStore.update({ [compId]: compProps })
						stateRefs.update({ [compId]: compStateRefs })
					}

					// Repeater items don't appear in the structure store but do appear in the props store
					if (!compStructure) {
						return
					}

					const loadComponentPromise = componentsLoader.loadComponent(
						compStructure.componentType,
						compStructure.uiType
					)

					updatePendingComponentClasses(loadComponentPromise, compStructure)

					// update stores and trigger componentWillMount
					const updatedStructure = {
						...compStructure,
						...(compStructure.components ? { components: [...compStructure.components] } : {}),
					}
					structure.update({ [compId]: updatedStructure })

					const contextId = structure.getContextIdOfCompId(compId) || 'masterPage'
					const componentWillMountPromises = await invokeComponentWillMountHandlersIfNeeded(
						contextId,
						compId,
						updatedStructure
					)

					await Promise.all([loadComponentPromise, ...componentWillMountPromises])
				},
				onStructureUpdate: ({ compId, compStructure }) => {
					if (isNewComp(compId, compStructure)) {
						return
					}
					const updatedStructure = {
						...compStructure,
						...(compStructure.components ? { components: [...compStructure.components] } : {}),
					}

					const updatesFromModel = difference(updatedStructure, structure.get(compId)) as Component
					if (!_.isEmpty(updatesFromModel)) {
						structure.update({ [compId]: updatesFromModel })
					}
				},
				onPropsUpdate: (compsProps) => {
					const updatedProps: PropsMap = {}
					const nonRuntimeUpdatedProps: PropsMap = {}

					_.forEach(compsProps, (compProps, compId) => {
						const updatedCompProps = difference(compProps, modelPropsStore.get(compId))
						const runtimeCompProps = difference(props.get(compId), modelPropsStore.get(compId))
						const nonRuntimeUpdatedCompProps = _.omitBy(
							updatedCompProps,
							(__, prop) => prop in runtimeCompProps
						)

						if (!_.isEmpty(updatedCompProps)) {
							updatedProps[compId] = updatedCompProps
						}

						if (!_.isEmpty(nonRuntimeUpdatedCompProps)) {
							// platform will run and update runtime props. in the meanwhile, we don't want to see a flicker.
							nonRuntimeUpdatedProps[compId] = nonRuntimeUpdatedCompProps
						}
					})

					modelPropsStore.update(updatedProps)
					props.update(nonRuntimeUpdatedProps)
				},
			},
			pendingRequirements: () =>
				Promise.all(Object.values(pendingComponentClasses)).then(() => Promise.resolve()),
		}
	}
)
